import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, TextField, Radio, RadioGroup, 
  FormControlLabel, FormControl, Button, IconButton, Stack,
  Chip, Pagination, CircularProgress, Divider
} from '@mui/material';
import { 
  Send as SendIcon, 
  EditOutlined as EditIcon, 
  ArchiveOutlined as ArchiveIcon, 
  DeleteOutline as DeleteIcon 
} from '@mui/icons-material';
import { db } from '../../firebase_config';
import { 
  collection, addDoc, getDocs, query, orderBy, 
  serverTimestamp, deleteDoc, doc, updateDoc 
} from 'firebase/firestore';

const NewsFeed = () => {
  // Form State
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [targetAudience, setTargetAudience] = useState('All Users');
  
  // Feed State
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pagination State (Client-side for simplicity, can be moved to server-side if collection grows massive)
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;

  const charCount = message.length;
  const maxChars = 500;

  // Fetch Announcements
  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'admin_announcements'), 
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const fetchedAnnouncements = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAnnouncements(fetchedAnnouncements);
    } catch (error) {
      console.error("Error fetching announcements: ", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // Handle Submit
  const handlePostAnnouncement = async () => {
    if (!title.trim() || !message.trim() || charCount > maxChars) return;

    setIsSubmitting(true);
    try {
      // Writing to admin_announcements triggers Iman's Cloud Function for FCM
      await addDoc(collection(db, 'admin_announcements'), {
        title: title.trim(),
        message: message.trim(),
        targetAudience,
        status: 'Active',
        createdAt: serverTimestamp()
      });

      // Reset form
      setTitle('');
      setMessage('');
      setTargetAudience('All Users');
      
      // Refresh list
      fetchAnnouncements();
    } catch (error) {
      console.error("Error posting announcement: ", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this announcement?")) {
      try {
        await deleteDoc(doc(db, 'admin_announcements', id));
        fetchAnnouncements();
      } catch (error) {
        console.error("Error deleting document: ", error);
      }
    }
  };

  // Handle Archive
  const handleArchive = async (id) => {
    try {
      await updateDoc(doc(db, 'admin_announcements', id), {
        status: 'Archived'
      });
      fetchAnnouncements();
    } catch (error) {
      console.error("Error archiving document: ", error);
    }
  };

  // Pagination logic
  const handleChangePage = (event, value) => {
    setPage(value);
  };
  const paginatedAnnouncements = announcements.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  // Format Date Helper
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: '1000px', margin: '0 auto' }}>
      
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>System Newsfeed</Typography>
          <Typography variant="body2" color="text.secondary">
            Post announcements and updates to users
          </Typography>
        </Box>
      </Box>

      {/* Create Announcement Section */}
      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, mb: 6, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Create Announcement</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Post system-wide announcements to users
        </Typography>

        <Stack spacing={3}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Title</Typography>
            <TextField 
              fullWidth 
              placeholder="Announcement title" 
              variant="outlined" 
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              size="small"
            />
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Message</Typography>
            <TextField 
              fullWidth 
              multiline 
              rows={4} 
              placeholder="Write your announcement message here..." 
              variant="outlined"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              error={charCount > maxChars}
            />
            <Typography variant="caption" color={charCount > maxChars ? "error" : "text.secondary"} sx={{ display: 'block', mt: 1 }}>
              {charCount}/{maxChars} characters
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Target Audience</Typography>
            <FormControl>
              <RadioGroup
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
              >
                <FormControlLabel value="All Users" control={<Radio sx={{ color: '#00796b', '&.Mui-checked': { color: '#00796b' } }} />} label={<Typography variant="body2">All Users (Students & Teachers)</Typography>} />
                <FormControlLabel value="All Students" control={<Radio sx={{ color: '#00796b', '&.Mui-checked': { color: '#00796b' } }} />} label={<Typography variant="body2">All Students</Typography>} />
                <FormControlLabel value="All Teachers" control={<Radio sx={{ color: '#00796b', '&.Mui-checked': { color: '#00796b' } }} />} label={<Typography variant="body2">All Teachers</Typography>} />
              </RadioGroup>
            </FormControl>
          </Box>

          <Button 
            variant="contained" 
            fullWidth 
            endIcon={<SendIcon />}
            onClick={handlePostAnnouncement}
            disabled={isSubmitting || !title || !message || charCount > maxChars}
            sx={{ 
              backgroundColor: '#00796b', 
              color: 'white', 
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#00695c' }
            }}
          >
            {isSubmitting ? 'Posting...' : 'Post Announcement'}
          </Button>
        </Stack>
      </Paper>

      {/* Recent Announcements Section */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Recent Announcements</Typography>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: '#00796b' }} />
        </Box>
      ) : (
        <Stack spacing={2}>
          {paginatedAnnouncements.map((announcement) => (
            <Paper 
              key={announcement.id} 
              elevation={0} 
              sx={{ 
                p: { xs: 2, md: 3 }, 
                borderRadius: 2, 
                border: '1px solid #e0e0e0',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{announcement.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {announcement.message}
                </Typography>
                
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(announcement.createdAt)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">•</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {announcement.targetAudience}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">•</Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ color: announcement.status === 'Active' ? '#00796b' : 'text.secondary', fontWeight: 500 }}
                  >
                    {announcement.status || 'Active'}
                  </Typography>
                </Stack>
              </Box>

              <Stack direction="row" spacing={1} sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
                <IconButton size="small" sx={{ color: 'text.secondary' }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleArchive(announcement.id)} sx={{ color: 'text.secondary' }}>
                  <ArchiveIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(announcement.id)} sx={{ color: '#d32f2f' }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Paper>
          ))}
          
          {announcements.length > itemsPerPage && (
             <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
               <Pagination 
                 count={Math.ceil(announcements.length / itemsPerPage)} 
                 page={page} 
                 onChange={handleChangePage} 
                 sx={{ '& .MuiPaginationItem-root.Mui-selected': { backgroundColor: '#e0f2f1', color: '#00796b' } }}
               />
             </Box>
          )}

          {announcements.length === 0 && !loading && (
             <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
               No announcements found.
             </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default NewsFeed;